import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, Route, Router, Switch } from 'react-router';
import { Box, Stack } from '@rossum/ui/material';
import { QUERY_KEY_QUEUES_UNPAGINATED } from '../business/queues/useUnpaginatedQueues';
import Announcements from '../components/Announcements';
import Navbar from '../components/NavBar';
import {
  ApprovalWorkflowsGuard,
  NonAdminRestrictor,
  queueSettingsRestrictorRoute,
  SettingsRestrictor,
  StatisticsRouteGuard,
} from '../components/Restrictors';
import { isEmbedded } from '../constants/config';
import RedirectToNewDashboard from '../containers/AnnotationList/RedirectToNewDashboard';
import { AutomationRoutes } from '../containers/Automation/AutomationRoutes';
import { automationPath } from '../containers/Automation/helpers';
import { ChangesSaved } from '../containers/ChangesSaved';
import { CHANGES_SAVED_PATH } from '../containers/ChangesSaved/constants';
import { DocumentValidationRoutes } from '../containers/DocumentValidation/DocumentValidationRoutes';
import IntegrationTest from '../containers/IntegrationTest';
import IntroScreen from '../containers/IntroScreen';
import Login from '../containers/Login';
import { SSOCallback } from '../containers/Login/components/SSOCallback';
import { SSORelay } from '../containers/Login/components/SSORelay';
import Maintenance from '../containers/Maintenance';
import Modals from '../containers/Modals';
import OrganizationLoader from '../containers/OrganizationLoader';
import PasswordConfirm from '../containers/Password/Confirm';
import PasswordReset from '../containers/Password/Reset';
import { settingsPath } from '../containers/Settings/helpers';
import { SettingsRoutes } from '../containers/Settings/SettingsRoutes';
import Statistics from '../containers/Statistics';
import TimeExpired from '../containers/TimeExpired';
import { WorkflowRoutes } from '../containers/WorkflowRoutes/WorkflowRoutes';
import RequireAuth from '../decorators/RequireAuth';
import { Billing } from '../features/billing';
import { DocumentList } from '../features/document-list/DocumentList';
import { ALL_DOCUMENTS_QUERY_KEY } from '../features/document-list/hooks/useFetchDashboardData';
import { QUERY_KEY_STATUS_COUNTS } from '../features/document-list/statuses/hooks/useStatusTabCounts';
import { SplitRoute } from '../features/document-split/SplitRoute';
import { ErrorPage } from '../features/error-page';
import {
  ExtensionInteropStartupHooks,
  ExtensionInteropUi,
} from '../features/extension-interop/ExtensionInteropUi';
import { TestIntent } from '../features/extension-interop/TestIntentRoute';
import { ExtensionsRoutes } from '../features/extensions/ExtensionsRoutes';
import { extensionsPath } from '../features/extensions/routes';
import { PersonalSettings } from '../features/personal-settings/PersonalSettings';
import { personalSettingsRoute } from '../features/personal-settings/routes';
import { QueueSettingsRoute } from '../features/queue-settings/routes/QueueSettingsRoute';
import { queueSettingsRoute } from '../features/queue-settings/routes/utils';
import { QuickSearch } from '../features/quick-search/QuickSearch';
import { ReportingRoutes } from '../features/reporting/routes';
import { DiscoveryCalls } from '../features/surveys/DiscoveryCallSurvey/DiscoveryCalls';
import { TaskContextProvider } from '../features/tasks/TaskContext';
import { TasksDialog } from '../features/tasks/TasksDialog';
import { ToasterContainer } from '../features/toaster';
import { usersPath } from '../features/users/routes';
import { useLogPageView } from '../lib/gtm';
import { history } from '../redux/configureStore';
import { useFeatureFlag } from '../unleash/useFeatureFlag';
import { ComponentsProvider } from './ComponentsProvider';
import { HelmetComponent } from './HelmetComponent';
import { useDefaultListBacklink } from './useAnnotationBacklink';

const Routes = () => {
  const queryClient = useQueryClient();
  useLogPageView(history);
  useDefaultListBacklink(history);

  const aggressiveRefetchEnabled = useFeatureFlag(
    'aggressive-dashboard-refetching'
  );

  const invalidateDashboardCounts = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_STATUS_COUNTS],
    });

    // not sure why we refetch queues but I left it as it was
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_QUEUES_UNPAGINATED],
    });
  }, [queryClient]);

  const invalidateDashboardQueries = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [ALL_DOCUMENTS_QUERY_KEY],
    });
  }, [queryClient]);

  const handleTaskFinished = useCallback(
    (_taskType: 'importTask') => {
      if (aggressiveRefetchEnabled) {
        invalidateDashboardQueries();
        invalidateDashboardCounts();
      }
    },
    [
      aggressiveRefetchEnabled,
      invalidateDashboardCounts,
      invalidateDashboardQueries,
    ]
  );

  // the user will always be defined under RequireAuth hence this flag should always be used within the scope of RequireAuth
  return (
    <Router history={history}>
      <ComponentsProvider>
        <ToasterContainer />
        <HelmetComponent />
        <Stack
          direction="column"
          sx={{
            height: '100vh',
            minHeight: '100vh',
            position: 'relative',
            zIndex: 1,
          }}
          className="basepageContainerLayout"
        >
          <Announcements />
          <Navbar />
          <Box
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Modals />
            <Switch>
              <Route path="/maintenance" component={Maintenance} />
              <Route path="/test-intent" render={() => <TestIntent />} />
              <Route path="/timeExpired" component={TimeExpired} />
              <Route path="/error" component={ErrorPage} />
              <Route path={CHANGES_SAVED_PATH} component={ChangesSaved} />
              <Route path="/recovery" component={PasswordReset} />
              <Route path="/resetPassword" component={PasswordConfirm} />
              <Route path="/integration-test" component={IntegrationTest} />
              <Route path="/mobileIntroScreen" component={IntroScreen} />
              <Route
                path="/organizationLoading"
                component={OrganizationLoader}
              />
              <Route exact path="/" component={Login} />
              <Route path="/sso-relay" component={SSORelay} />
              <Route path="/sso-callback" component={SSOCallback} />
              <RequireAuth>
                <TaskContextProvider
                  onUploadCreated={() => {
                    invalidateDashboardCounts();
                    invalidateDashboardQueries();
                  }}
                >
                  <TasksDialog onTaskFinished={handleTaskFinished} />
                  <Switch>
                    <Route
                      path="/queues/:queueId"
                      exact
                      component={RedirectToNewDashboard}
                    />
                    <Route path="/documents" component={DocumentList} />
                    <Route
                      path="/annotations/:queueId?"
                      component={RedirectToNewDashboard}
                    />
                    <Route
                      path="/emails/:queueId?"
                      component={RedirectToNewDashboard}
                    />
                    <Route
                      path={[
                        '/split/:annotationId',
                        '/embedded/split/:annotationId',
                      ]}
                      render={() => <SplitRoute />}
                    />
                    <Route
                      path={[
                        '/document/:annotationId?',
                        '/embedded/document/:annotationId?',
                      ]}
                      render={() => <DocumentValidationRoutes />}
                    />

                    <Route
                      path="/requests"
                      render={() => (
                        <ApprovalWorkflowsGuard>
                          <WorkflowRoutes />
                        </ApprovalWorkflowsGuard>
                      )}
                    />
                    <Route
                      path={[automationPath(), extensionsPath()]}
                      render={() => (
                        <NonAdminRestrictor
                          restrictComponent={<Redirect to="/documents" />}
                        >
                          <Switch>
                            <Route
                              path={automationPath()}
                              component={AutomationRoutes}
                            />
                            <Route
                              path={[extensionsPath()]}
                              render={() => <ExtensionsRoutes />}
                            />
                          </Switch>
                        </NonAdminRestrictor>
                      )}
                    />
                    <Route
                      path={settingsPath()}
                      render={routeProps => (
                        <SettingsRestrictor
                          restrictComponent={<Redirect to="/documents" />}
                        >
                          <SettingsRoutes {...routeProps} />
                        </SettingsRestrictor>
                      )}
                    />
                    <Route
                      path={['/users', '/users/:id']}
                      render={({ location }) => (
                        <Redirect
                          to={location.pathname.replace('/users', usersPath())}
                        />
                      )}
                    />
                    <Route path={queueSettingsRoute()}>
                      {queueSettingsRestrictorRoute(routeProps => (
                        <QueueSettingsRoute {...routeProps} />
                      ))}
                    </Route>
                    <Route
                      path={personalSettingsRoute}
                      component={PersonalSettings}
                    />
                    <Route path="/billing" component={Billing} />
                    <Route
                      path="/reporting"
                      render={routeProps => <ReportingRoutes {...routeProps} />}
                    />
                    <Route
                      path="/statistics"
                      render={props => (
                        <StatisticsRouteGuard>
                          <Statistics {...props} />
                        </StatisticsRouteGuard>
                      )}
                    />
                    <Route
                      render={() => <Redirect to="/error?reason=not-found" />}
                    />
                  </Switch>
                </TaskContextProvider>
                <ExtensionInteropStartupHooks />
                <ExtensionInteropUi />
                {!isEmbedded() && <QuickSearch />}
                <DiscoveryCalls />
              </RequireAuth>
            </Switch>
          </Box>
        </Stack>
      </ComponentsProvider>
    </Router>
  );
};

export default Routes;
